import { StripeCheckoutPaymentMethodEnum } from '@/gen/cortado/v1/settings_pb'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { QuoteBuilderPaymentCard } from '@/components/quotes/quote-builder-payment-card'
import {
  appendQuoteFixedPaymentScheduleForTotal,
  rebalanceQuoteFixedPaymentSchedulesForTotal,
} from '@/lib/payment-schedule-utils'
import { DEFAULT_CHECKOUT_PAYMENT_METHODS, type QuoteEditorFormValues } from './quote-editor-form'

type PaymentValues = Pick<
  QuoteEditorFormValues,
  'paymentSchedules' | 'useCustomPaymentMethods' | 'checkoutPaymentMethods' | 'requiresQuestionnaire'
>

interface QuoteEditorPaymentProps {
  values: PaymentValues
  onChange: (patch: Partial<PaymentValues>) => void
  /** Tip-exclusive quote total the schedule must cover. */
  totalCents: number
  /** Enforced tip, collected with the last payment. */
  tipCents: number
  eventDates: Array<Date | undefined>
  depositDueDays?: number
  remainderDueDays?: number
  disabled: boolean
}

const CARD = StripeCheckoutPaymentMethodEnum.STRIPE_CHECKOUT_PAYMENT_METHOD_CARD
const ACH = StripeCheckoutPaymentMethodEnum.STRIPE_CHECKOUT_PAYMENT_METHOD_US_BANK_ACCOUNT

/**
 * Payment for the quote editor: the payment schedule (presets, per-payment
 * amounts and due anchors, and the rebalance actions when fixed amounts no
 * longer match the total), checkout methods, and the questionnaire toggle.
 */
export function QuoteEditorPayment({
  values,
  onChange,
  totalCents,
  tipCents,
  eventDates,
  depositDueDays,
  remainderDueDays,
  disabled,
}: QuoteEditorPaymentProps) {
  const methods = values.checkoutPaymentMethods
  function toggleMethod(method: StripeCheckoutPaymentMethodEnum, checked: boolean) {
    const next = checked ? [...methods.filter((candidate) => candidate !== method), method] : methods.filter((candidate) => candidate !== method)
    // At least one method stays selected.
    if (next.length > 0) onChange({ checkoutPaymentMethods: next })
  }

  return (
    <div className="space-y-4">
      <QuoteBuilderPaymentCard
        sectionId="quote-editor-payment"
        title="Payment"
        schedules={values.paymentSchedules}
        totalCents={totalCents}
        onSchedulesChange={(paymentSchedules) => onChange({ paymentSchedules })}
        onApplyBalanceToLastPayment={() =>
          onChange({ paymentSchedules: rebalanceQuoteFixedPaymentSchedulesForTotal(values.paymentSchedules, totalCents) })
        }
        onAddBalanceAsNewPayment={() =>
          onChange({ paymentSchedules: appendQuoteFixedPaymentScheduleForTotal(values.paymentSchedules, totalCents) })
        }
        useCustomPaymentMethods={values.useCustomPaymentMethods}
        onUseCustomPaymentMethodsChange={(checked) =>
          onChange({
            useCustomPaymentMethods: checked,
            ...(checked ? {} : { checkoutPaymentMethods: [...DEFAULT_CHECKOUT_PAYMENT_METHODS] }),
          })
        }
        paymentMethodsControl={
          <div className="grid gap-2 sm:grid-cols-2">
            <label htmlFor="quote-editor-method-card" className="flex min-h-11 cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm leading-none font-normal select-none">
              <Checkbox
                id="quote-editor-method-card"
                checked={methods.includes(CARD)}
                onCheckedChange={(checked) => toggleMethod(CARD, checked === true)}
                disabled={disabled || (methods.includes(CARD) && !methods.includes(ACH))}
              />
              Credit Card
            </label>
            <label htmlFor="quote-editor-method-ach" className="flex min-h-11 cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm leading-none font-normal select-none">
              <Checkbox
                id="quote-editor-method-ach"
                checked={methods.includes(ACH)}
                onCheckedChange={(checked) => toggleMethod(ACH, checked === true)}
                disabled={disabled || (methods.includes(ACH) && !methods.includes(CARD))}
              />
              ACH / Bank Transfer
            </label>
          </div>
        }
        depositDueDays={depositDueDays}
        remainderDueDays={remainderDueDays}
        eventDates={eventDates}
        disabled={disabled}
        editableDueAnchors
        lastPaymentTipCents={tipCents}
      />
      <div className="flex items-center justify-between gap-3 rounded-xl border px-4 py-3">
        <div>
          <Label htmlFor="quote-editor-questionnaire">Require questionnaire</Label>
          <p className="mt-1 text-xs text-muted-foreground">Accepting the quote attaches a questionnaire to the booking.</p>
        </div>
        <Switch
          id="quote-editor-questionnaire"
          checked={values.requiresQuestionnaire}
          onCheckedChange={(requiresQuestionnaire) => onChange({ requiresQuestionnaire })}
          disabled={disabled}
        />
      </div>
    </div>
  )
}
